/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChatIcon from "@mui/icons-material/Chat";
import EventIcon from "@mui/icons-material/Event";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import StarIcon from "@mui/icons-material/Star";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";
import { API_BASE, API_HOST } from "config/api";

// ── helpers ────────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return { Accept: "application/json", Authorization: `Bearer ${token}` };
}

async function apiFetch(path, options = {}) {
  const { headers: extraHeaders, params, ...rest } = options;
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await window.fetch(`${API_BASE}/${path}${qs}`, {
    headers: { ...authHeaders(), ...(extraHeaders || {}) },
    ...rest,
  });
  return res;
}

function groupParams(group) {
  if (!group) return {};
  return { group_type: group.group_type, group_id: group.group_id };
}

function MemberAvatar({ member, size = 40 }) {
  const photo = member?.photo;
  const name = member ? `${member.first_name} ${member.surname}` : "?";
  return (
    <Avatar
      src={photo ? `${API_HOST}/storage/${photo}` : undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        bgcolor: "primary.main",
        fontSize: size * 0.4,
        flexShrink: 0,
      }}
    >
      {!photo && <PersonIcon sx={{ fontSize: size * 0.55 }} />}
    </Avatar>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ── MembersTab ─────────────────────────────────────────────────────────────────

function MembersTab({ group }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);

  const load = useCallback(() => {
    if (!group) return;
    setLoading(true);
    apiFetch("my-group/members", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setMembers(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!addOpen) return;
    const timer = setTimeout(() => {
      if (!group) return;
      setSearching(true);
      apiFetch("my-group/search-members", { params: { ...groupParams(group), q: searchQ } })
        .then((r) => r.json())
        .then((d) => setSearchResults(d.data || []))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQ, addOpen, group?.group_type, group?.group_id]);

  const openAdd = () => {
    setSearchQ("");
    setSearchResults([]);
    setAddOpen(true);
  };

  const handleAdd = async (memberId) => {
    setAdding(memberId);
    try {
      const res = await apiFetch("my-group/members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify({ member_id: memberId }),
      });
      if (res.ok) {
        load();
        setSearchResults((prev) => prev.filter((m) => m.id !== memberId));
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not add member", "error");
      }
    } catch {
      /* silent */
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (memberId, name) => {
    const result = await Swal.fire({
      title: `Remove ${name}?`,
      text: "They will be removed from this group.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d32f2f",
    });
    if (!result.isConfirmed) return;
    const res = await apiFetch(`my-group/members/${memberId}`, {
      method: "DELETE",
      params: groupParams(group),
    });
    if (res.ok) {
      load();
    } else {
      const d = await res.json();
      Swal.fire("Error", d.message || "Could not remove member", "error");
    }
  };

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Typography variant="caption" color="text.secondary">
          {members.length} member{members.length !== 1 ? "s" : ""}
        </Typography>
        {group?.can_manage && (
          <Button startIcon={<PersonAddIcon />} variant="contained" size="small" onClick={openAdd}>
            Add Member
          </Button>
        )}
      </Box>

      <Grid container spacing={1.5}>
        {members.map((m) => {
          const isGroupLeader = group?.leader?.id === m.id;
          return (
            <Grid item xs={12} sm={6} md={4} key={m.id}>
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                p={1.5}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isGroupLeader ? "primary.main" : "divider",
                  bgcolor: isGroupLeader ? "primary.lighter" : "background.paper",
                }}
              >
                <MemberAvatar member={m} size={40} />
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {m.first_name} {m.surname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {m.phone || m.email || m.memberID}
                  </Typography>
                  {isGroupLeader && (
                    <Chip
                      label="Leader"
                      size="small"
                      color="primary"
                      sx={{ height: 18, fontSize: 10, mt: 0.25 }}
                    />
                  )}
                </Box>
                {group?.can_manage && !isGroupLeader && (
                  <Tooltip title="Remove from group">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(m.id, `${m.first_name} ${m.surname}`)}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          );
        })}
        {members.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" textAlign="center" py={4}>
              No members yet.
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Add Member Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <DialogTitle>Add Member to Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or member ID…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            sx={{ mt: 1, mb: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          {searching && (
            <Box textAlign="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          <List dense disablePadding>
            {searchResults.map((m) => (
              <ListItem
                key={m.id}
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={adding === m.id}
                    onClick={() => handleAdd(m.id)}
                  >
                    {adding === m.id ? <CircularProgress size={14} /> : "Add"}
                  </Button>
                }
              >
                <ListItemAvatar>
                  <MemberAvatar member={m} size={36} />
                </ListItemAvatar>
                <ListItemText
                  primary={`${m.first_name} ${m.surname}`}
                  secondary={m.memberID || m.phone || ""}
                />
              </ListItem>
            ))}
            {!searching && searchResults.length === 0 && searchQ && (
              <Typography color="text.secondary" textAlign="center" py={2} variant="body2">
                No church members found matching &ldquo;{searchQ}&rdquo;
              </Typography>
            )}
            {!searching && searchResults.length === 0 && !searchQ && (
              <Typography color="text.secondary" textAlign="center" py={2} variant="body2">
                Type a name to search church members
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── ChatTab ────────────────────────────────────────────────────────────────────

function ChatTab({ group }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const memberId = user?.member_id;

  const load = useCallback(() => {
    if (!group) return;
    apiFetch("my-group/messages", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setMessages(d.data || []))
      .catch(() => {});
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await apiFetch("my-group/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify({ message: text.trim() }),
      });
      if (res.ok) {
        setText("");
        load();
      }
    } catch {
      /* silent */
    } finally {
      setSending(false);
    }
  };

  const del = async (id) => {
    const result = await Swal.fire({
      title: "Delete message?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    await apiFetch(`my-group/messages/${id}`, { method: "DELETE", params: groupParams(group) });
    load();
  };

  return (
    <Box display="flex" flexDirection="column" sx={{ height: { xs: 380, sm: 460 } }}>
      <Box flex={1} overflow="auto" px={0.5} py={1}>
        {messages.map((m) => {
          const isMe = parseInt(m.member_id, 10) === parseInt(memberId, 10);
          return (
            <Box
              key={m.id}
              display="flex"
              flexDirection={isMe ? "row-reverse" : "row"}
              alignItems="flex-end"
              gap={0.75}
              mb={1}
            >
              {!isMe && <MemberAvatar member={m.member} size={26} />}
              <Box
                sx={{
                  maxWidth: { xs: "85%", sm: "65%" },
                  bgcolor: isMe ? "primary.main" : "grey.100",
                  color: isMe ? "white" : "text.primary",
                  borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  px: 1.5,
                  py: 0.75,
                }}
              >
                {!isMe && (
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    display="block"
                    sx={{ opacity: 0.75 }}
                  >
                    {m.member?.first_name}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {m.message}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.6, float: "right", ml: 1, mt: 0.25 }}
                >
                  {formatTime(m.created_at)}
                </Typography>
              </Box>
              {(isMe || group?.can_manage) && (
                <IconButton
                  size="small"
                  onClick={() => del(m.id)}
                  sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }}
                >
                  <DeleteIcon sx={{ fontSize: 13 }} />
                </IconButton>
              )}
            </Box>
          );
        })}
        {messages.length === 0 && (
          <Typography textAlign="center" color="text.secondary" py={4}>
            No messages yet. Start the conversation!
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>
      <Divider />
      <Box display="flex" gap={1} pt={1.5} alignItems="flex-end">
        <TextField
          size="small"
          placeholder="Type a message…"
          fullWidth
          multiline
          maxRows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton color="primary" onClick={send} disabled={sending || !text.trim()}>
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ── AnnouncementsTab ───────────────────────────────────────────────────────────

function AnnouncementsTab({ group }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!group) return;
    apiFetch("my-group/announcements", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setAnnouncements(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("my-group/announcements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setCreateOpen(false);
        setForm({ title: "", body: "" });
        load();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not post", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    const result = await Swal.fire({
      title: "Delete announcement?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await apiFetch(`my-group/announcements/${id}`, {
      method: "DELETE",
      params: groupParams(group),
    });
    load();
  };

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  return (
    <Box>
      {group?.can_manage && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setCreateOpen(true)}
          >
            New Announcement
          </Button>
        </Box>
      )}
      {announcements.length === 0 && (
        <Typography textAlign="center" color="text.secondary" py={4}>
          No announcements yet.
        </Typography>
      )}
      {announcements.map((a) => (
        <Card key={a.id} variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {a.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.author ? `${a.author.first_name} ${a.author.surname}` : "—"} ·{" "}
                  {formatTime(a.created_at)}
                </Typography>
              </Box>
              {group?.can_manage && (
                <IconButton size="small" color="error" onClick={() => del(a.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Typography variant="body2" mt={1} sx={{ whiteSpace: "pre-wrap" }}>
              {a.body}
            </Typography>
          </CardContent>
        </Card>
      ))}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <DialogTitle>New Announcement</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            size="small"
            margin="normal"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
          <TextField
            label="Body"
            fullWidth
            size="small"
            margin="normal"
            multiline
            rows={4}
            value={form.body}
            onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={post} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Post"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── EventsTab ──────────────────────────────────────────────────────────────────

function EventsTab({ group }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!group) return;
    setLoading(true);
    apiFetch("my-group/events", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setEvents(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );
  if (events.length === 0)
    return (
      <Typography textAlign="center" color="text.secondary" py={4}>
        No events for this group.
      </Typography>
    );

  return (
    <Grid container spacing={1.5}>
      {events.map((e) => (
        <Grid item xs={12} sm={6} key={e.id}>
          <Box
            p={1.5}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: `4px solid ${e.is_done ? "#9e9e9e" : "#0097a7"}`,
            }}
          >
            <Typography variant="button" fontWeight={700} display="block">
              {e.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {new Date(e.start_at).toLocaleString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
              {e.location ? ` · ${e.location}` : ""}
            </Typography>
            {e.is_done && <Chip label="Done" size="small" sx={{ mt: 0.5 }} />}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ── DuesTab ────────────────────────────────────────────────────────────────────

function DuePaymentsPanel({ due, group, groupMembers }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({
    member_id: "",
    amount_paid: due?.amount || "",
    paid_date: new Date().toISOString().slice(0, 10),
    payment_method: "Cash",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!due?.id) return;
    apiFetch(`my-group/dues/${due.id}/payments`, { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setPayments(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [due?.id, group?.group_type, group?.group_id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const recordPayment = async () => {
    if (!form.member_id || !form.amount_paid) return;
    setSaving(true);
    try {
      const res = await apiFetch(`my-group/dues/${due.id}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setPayOpen(false);
        setForm((p) => ({ ...p, member_id: "", notes: "" }));
        load();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not record payment", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (id) => {
    const result = await Swal.fire({
      title: "Delete payment record?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await apiFetch(`my-group/dues/payments/${id}`, {
      method: "DELETE",
      params: groupParams(group),
    });
    load();
  };

  const totalCollected = payments.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0);
  const paidMemberIds = payments.map((p) => p.member_id);

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setOpen((v) => !v)}
        sx={{ mt: 0.5 }}
      >
        {payments.length > 0 || !loading
          ? `${payments.length} payment${
              payments.length !== 1 ? "s" : ""
            } · GHS ${totalCollected.toFixed(2)}`
          : "View payments"}
      </Button>

      <Collapse in={open}>
        <Box sx={{ mt: 1, pl: 0.5 }}>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {payments.map((p) => (
                <Box
                  key={p.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={0.75}
                  p={1}
                  sx={{ borderRadius: 1, bgcolor: "success.lighter" }}
                >
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 16 }} />
                  <Box flex={1} minWidth={0}>
                    <Typography variant="caption" fontWeight={700} noWrap>
                      {p.member?.first_name} {p.member?.surname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      GHS {Number(p.amount_paid).toFixed(2)} ·{" "}
                      {p.paid_date ? new Date(p.paid_date).toLocaleDateString() : "—"}
                      {p.payment_method ? ` · ${p.payment_method}` : ""}
                    </Typography>
                  </Box>
                  {group?.can_manage && (
                    <IconButton size="small" color="error" onClick={() => deletePayment(p.id)}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
              {payments.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No payments recorded yet.
                </Typography>
              )}
              {group?.can_manage && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    setForm((p) => ({
                      ...p,
                      member_id: "",
                      amount_paid: due?.amount || "",
                      notes: "",
                    }));
                    setPayOpen(true);
                  }}
                >
                  Record Payment
                </Button>
              )}
            </>
          )}
        </Box>
      </Collapse>

      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
      >
        <DialogTitle>
          Record Payment — {MONTHS[(due?.due_month || 1) - 1]} {due?.due_year}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Member</InputLabel>
            <Select
              value={form.member_id}
              label="Member"
              onChange={(e) => setForm((p) => ({ ...p, member_id: e.target.value }))}
            >
              {groupMembers.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {paidMemberIds.includes(m.id) && (
                      <CheckCircleIcon sx={{ color: "success.main", fontSize: 14 }} />
                    )}
                    {m.first_name} {m.surname}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Amount Paid (GHS)"
            type="number"
            fullWidth
            size="small"
            margin="normal"
            value={form.amount_paid}
            onChange={(e) => setForm((p) => ({ ...p, amount_paid: e.target.value }))}
          />
          <TextField
            label="Date Paid"
            type="date"
            fullWidth
            size="small"
            margin="normal"
            value={form.paid_date}
            onChange={(e) => setForm((p) => ({ ...p, paid_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={form.payment_method}
              label="Payment Method"
              onChange={(e) => setForm((p) => ({ ...p, payment_method: e.target.value }))}
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="MoMo">MoMo</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Cheque">Cheque</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            fullWidth
            size="small"
            margin="normal"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button onClick={recordPayment} variant="contained" disabled={saving || !form.member_id}>
            {saving ? <CircularProgress size={18} /> : "Record"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DuesTab({ group }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [dues, setDues] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    due_month: new Date().getMonth() + 1,
    due_year: new Date().getFullYear(),
    notes: "",
    status: "pending",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!group) return;
    apiFetch("my-group/dues", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setDues(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    load();
    if (group) {
      apiFetch("my-group/members", { params: groupParams(group) })
        .then((r) => r.json())
        .then((d) => setGroupMembers(d.data || []));
    }
  }, [load, group?.group_type, group?.group_id]);

  const openCreate = () => {
    setEditItem(null);
    setForm({
      amount: "",
      due_month: new Date().getMonth() + 1,
      due_year: new Date().getFullYear(),
      notes: "",
      status: "pending",
    });
    setCreateOpen(true);
  };

  const openEdit = (due) => {
    setEditItem(due);
    setForm({
      amount: due.amount,
      due_month: due.due_month,
      due_year: due.due_year,
      notes: due.notes || "",
      status: due.status,
    });
    setCreateOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const path = editItem ? `my-group/dues/${editItem.id}` : "my-group/dues";
      const method = editItem ? "PUT" : "POST";
      const res = await apiFetch(path, {
        method,
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setCreateOpen(false);
        load();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not save", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (s) => (s === "paid" ? "success" : s === "overdue" ? "error" : "warning");
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 1);

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  return (
    <Box>
      {group?.can_manage && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openCreate}>
            Set Monthly Due
          </Button>
        </Box>
      )}

      {dues.length === 0 && (
        <Typography textAlign="center" color="text.secondary" py={4}>
          No dues records yet.
        </Typography>
      )}

      <Grid container spacing={1.5}>
        {dues.map((d) => (
          <Grid item xs={12} sm={6} md={4} key={d.id}>
            <Box p={1.5} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {MONTHS[(d.due_month || 1) - 1]} {d.due_year}
                </Typography>
                <Chip label={d.status} size="small" color={statusColor(d.status)} />
              </Box>
              <Typography variant="h6" fontWeight={800}>
                GHS {Number(d.amount).toFixed(2)}
              </Typography>
              {d.notes && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {d.notes}
                </Typography>
              )}
              {group?.can_manage && (
                <Box mt={0.5} display="flex" gap={1}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(d)}>
                    Edit
                  </Button>
                </Box>
              )}
              <DuePaymentsPanel due={d} group={group} groupMembers={groupMembers} />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
      >
        <DialogTitle>{editItem ? "Edit Due" : "Set Monthly Due"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount (GHS)"
            type="number"
            fullWidth
            size="small"
            margin="normal"
            value={form.amount}
            onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
          />
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Month</InputLabel>
            <Select
              value={form.due_month}
              label="Month"
              onChange={(e) => setForm((p) => ({ ...p, due_month: e.target.value }))}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={i} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Year</InputLabel>
            <Select
              value={form.due_year}
              label="Year"
              onChange={(e) => setForm((p) => ({ ...p, due_year: e.target.value }))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              label="Status"
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            fullWidth
            size="small"
            margin="normal"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── ContributionsTab ───────────────────────────────────────────────────────────

function ContributionsTab({ group }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_amount: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!group) return;
    apiFetch("my-group/contributions", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setItems(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("my-group/contributions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setCreateOpen(false);
        setForm({ title: "", description: "", target_amount: "" });
        load();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not create", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    const result = await Swal.fire({
      title: "Delete this contribution goal?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await apiFetch(`my-group/contributions/${id}`, {
      method: "DELETE",
      params: groupParams(group),
    });
    load();
  };

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  return (
    <Box>
      {group?.can_manage && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setCreateOpen(true)}
          >
            New Goal
          </Button>
        </Box>
      )}
      {items.length === 0 && (
        <Typography textAlign="center" color="text.secondary" py={4}>
          No contribution goals yet.
        </Typography>
      )}
      <Grid container spacing={1.5}>
        {items.map((c) => (
          <Grid item xs={12} sm={6} key={c.id}>
            <Card variant="outlined">
              <CardContent sx={{ pb: "12px !important" }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {c.title}
                    </Typography>
                    {c.target_amount && (
                      <Typography variant="caption" color="text.secondary">
                        Target: GHS {Number(c.target_amount).toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  {group?.can_manage && (
                    <IconButton size="small" color="error" onClick={() => del(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {c.description && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {c.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {c.creator ? `Added by ${c.creator.first_name} ${c.creator.surname}` : ""} ·{" "}
                  {formatTime(c.created_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
      >
        <DialogTitle>New Contribution Goal</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            size="small"
            margin="normal"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            size="small"
            margin="normal"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
          <TextField
            label="Target Amount (GHS)"
            type="number"
            fullWidth
            size="small"
            margin="normal"
            value={form.target_amount}
            onChange={(e) => setForm((p) => ({ ...p, target_amount: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={create} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── ExecutivesTab ──────────────────────────────────────────────────────────────

function ExecutivesTab({ group }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [executives, setExecutives] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [newRole, setNewRole] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadExecs = useCallback(() => {
    if (!group) return;
    apiFetch("my-group/executives", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setExecutives(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  useEffect(() => {
    loadExecs();
  }, [loadExecs]);

  const openAdd = () => {
    apiFetch("my-group/members", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setGroupMembers(d.data || []));
    setSelectedMemberId("");
    setNewRole("");
    setAddOpen(true);
  };

  const openEditRole = (exec) => {
    setEditTarget(exec);
    setNewRole(exec.exec_role || "");
    setEditRoleOpen(true);
  };

  const add = async () => {
    if (!selectedMemberId) return;
    setSaving(true);
    try {
      const res = await apiFetch("my-group/executives", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify({ member_id: Number(selectedMemberId), role: newRole || null }),
      });
      if (res.ok) {
        setAddOpen(false);
        loadExecs();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not add executive", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const res = await apiFetch(`my-group/executives/${editTarget.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        params: groupParams(group),
        body: JSON.stringify({ role: newRole || null }),
      });
      if (res.ok) {
        setEditRoleOpen(false);
        loadExecs();
      } else {
        const d = await res.json();
        Swal.fire("Error", d.message || "Could not update role", "error");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const remove = async (memberId) => {
    const result = await Swal.fire({
      title: "Remove executive?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await apiFetch(`my-group/executives/${memberId}`, {
      method: "DELETE",
      params: groupParams(group),
    });
    loadExecs();
  };

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  return (
    <Box>
      {group?.is_leader && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAdd}>
            Add Executive
          </Button>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" mb={2} display="block">
        Executives share management privileges with the group leader.
      </Typography>

      {executives.length === 0 && (
        <Typography textAlign="center" color="text.secondary" py={4}>
          No executives yet.
        </Typography>
      )}

      <Grid container spacing={1.5}>
        {executives.map((m) => (
          <Grid item xs={12} sm={6} md={4} key={m?.id}>
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              p={1.5}
              sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}
            >
              <MemberAvatar member={m} size={40} />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {m?.first_name} {m?.surname}
                </Typography>
                <Chip
                  label={m?.exec_role || "Executive"}
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: 10, mt: 0.25 }}
                />
              </Box>
              {group?.is_leader && (
                <Box display="flex" flexDirection="column" gap={0.25}>
                  <Tooltip title="Edit role">
                    <IconButton size="small" onClick={() => openEditRole(m)}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" color="error" onClick={() => remove(m?.id)}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Add Executive Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
      >
        <DialogTitle>Add Executive</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Select Member</InputLabel>
            <Select
              value={selectedMemberId}
              label="Select Member"
              onChange={(e) => setSelectedMemberId(e.target.value)}
            >
              {groupMembers.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.first_name} {m.surname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Position / Role (e.g. Secretary)"
            fullWidth
            size="small"
            margin="normal"
            placeholder="Financial Secretary, Social Secretary…"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button onClick={add} variant="contained" disabled={saving || !selectedMemberId}>
            {saving ? <CircularProgress size={18} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editRoleOpen}
        onClose={() => setEditRoleOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
      >
        <DialogTitle>
          Update Role — {editTarget?.first_name} {editTarget?.surname}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Position / Role"
            fullWidth
            size="small"
            margin="normal"
            placeholder="Secretary, Treasurer, Financial Secretary…"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleOpen(false)}>Cancel</Button>
          <Button onClick={updateRole} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── ReportsTab ─────────────────────────────────────────────────────────────────

function ReportsTab({ group }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!group) return;
    setLoading(true);
    apiFetch("my-group/reports/dues", { params: groupParams(group) })
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.group_type, group?.group_id]);

  if (loading)
    return (
      <Box p={3} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );

  if (!data || data.dues?.length === 0 || data.members?.length === 0) {
    return (
      <Typography textAlign="center" color="text.secondary" py={4}>
        No dues data to report yet. Set monthly dues and record payments to see the report.
      </Typography>
    );
  }

  const { dues, members, payment_map } = data;

  const paidCount = (dueId) => members.filter((m) => payment_map?.[m.id]?.[dueId]).length;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        Dues Payment Report
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        {members.length} member{members.length !== 1 ? "s" : ""} · {dues.length} due period
        {dues.length !== 1 ? "s" : ""}
      </Typography>

      <TableContainer
        sx={{ overflowX: "auto", borderRadius: 2, border: "1px solid", borderColor: "divider" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: "grey.50",
                  minWidth: 130,
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                }}
              >
                Member
              </TableCell>
              {dues.map((d) => (
                <TableCell
                  key={d.id}
                  align="center"
                  sx={{ fontWeight: 700, bgcolor: "grey.50", minWidth: 80 }}
                >
                  {MONTHS[d.due_month - 1].slice(0, 3)}
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {d.due_year}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "grey.50", minWidth: 70 }}>
                Paid
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => {
              const paidDues = dues.filter((d) => payment_map?.[m.id]?.[d.id]);
              return (
                <TableRow key={m.id} hover>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <MemberAvatar member={m} size={28} />
                      <Typography variant="caption" fontWeight={600} noWrap>
                        {m.first_name} {m.surname}
                      </Typography>
                    </Box>
                  </TableCell>
                  {dues.map((d) => {
                    const payment = payment_map?.[m.id]?.[d.id];
                    return (
                      <TableCell key={d.id} align="center">
                        {payment ? (
                          <Tooltip title={`GHS ${Number(payment.amount_paid).toFixed(2)}`}>
                            <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={paidDues.length === dues.length ? "success.main" : "text.secondary"}
                    >
                      {paidDues.length}/{dues.length}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Summary row */}
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell
                sx={{ fontWeight: 700, position: "sticky", left: 0, bgcolor: "grey.50", zIndex: 1 }}
              >
                Total Paid
              </TableCell>
              {dues.map((d) => (
                <TableCell key={d.id} align="center">
                  <Typography variant="caption" fontWeight={700}>
                    {paidCount(d.id)}/{members.length}
                  </Typography>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── MyGroup (main) ─────────────────────────────────────────────────────────────

export default function MyGroup() {
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    apiFetch("my-group/all")
      .then((r) => r.json())
      .then((d) => {
        setGroups(d.groups || []);
        setLoadingGroups(false);
      })
      .catch(() => setLoadingGroups(false));
  }, []);

  const group = groups[selectedIdx] || null;

  const switchGroup = (idx) => {
    setSelectedIdx(idx);
    setTab(0);
  };

  const tabs = group
    ? [
        { label: "Members", icon: <GroupsIcon fontSize="small" /> },
        { label: "Chat", icon: <ChatIcon fontSize="small" /> },
        { label: "Posts", icon: <CampaignIcon fontSize="small" /> },
        { label: "Events", icon: <EventIcon fontSize="small" /> },
        { label: "Dues", icon: <PaymentsIcon fontSize="small" /> },
        { label: "Funds", icon: <VolunteerActivismIcon fontSize="small" /> },
        ...(group.can_manage
          ? [
              { label: "Executives", icon: <StarIcon fontSize="small" /> },
              { label: "Reports", icon: <AssessmentIcon fontSize="small" /> },
            ]
          : []),
      ]
    : [];

  if (loadingGroups) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <>
        <PageHeader title="My Group" subtitle="Ministry & department community" />
        <Box textAlign="center" py={8}>
          <GroupsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">
            You are not yet assigned to a ministry or department.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Contact the church office to be added to a group.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageHeader title="My Group" subtitle="Ministry & department community" />

      {/* Group selector */}
      {groups.length > 1 && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {groups.map((g, i) => (
            <Chip
              key={i}
              label={`${g.name} · ${g.group_type === "ministry" ? "Ministry" : "Dept"}`}
              onClick={() => switchGroup(i)}
              color={selectedIdx === i ? "primary" : "default"}
              variant={selectedIdx === i ? "filled" : "outlined"}
              icon={g.is_leader ? <StarIcon sx={{ fontSize: "14px !important" }} /> : undefined}
              sx={{ maxWidth: { xs: 180, sm: "none" } }}
            />
          ))}
        </Box>
      )}

      {/* Group header */}
      {group && (
        <Card
          sx={{
            mb: 2,
            background: "linear-gradient(135deg, #0097a7 0%, #00bcd4 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
            >
              <Box flex={1} minWidth={0}>
                <Typography variant="overline" sx={{ opacity: 0.85, fontSize: { xs: 10, sm: 12 } }}>
                  {group.group_type === "ministry" ? "Ministry" : "Department"}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" }, wordBreak: "break-word" }}
                >
                  {group.name}
                </Typography>
                {group.description && (
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, mt: 0.25, fontSize: { xs: 12, sm: 14 } }}
                  >
                    {group.description}
                  </Typography>
                )}
              </Box>
              <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
                <Chip
                  label={`${group.member_count} member${group.member_count !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: 11 }}
                />
                {group.is_leader && (
                  <Chip
                    label="Leader"
                    size="small"
                    icon={
                      <StarIcon sx={{ color: "white !important", fontSize: "13px !important" }} />
                    }
                    sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white", fontSize: 11 }}
                  />
                )}
                {!group.is_leader && group.is_exec && (
                  <Chip
                    label={group.exec_role || "Executive"}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: 11 }}
                  />
                )}
              </Box>
            </Box>
            {group.leader && (
              <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                <MemberAvatar member={group.leader} size={26} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Led by {group.leader.first_name} {group.leader.surname}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
              minWidth: { xs: 72, sm: 100 },
              fontSize: { xs: 12, sm: 14 },
              px: { xs: 1, sm: 2 },
            },
          }}
        >
          {tabs.map((t, i) => (
            <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {tab === 0 && <MembersTab group={group} />}
          {tab === 1 && <ChatTab group={group} />}
          {tab === 2 && <AnnouncementsTab group={group} />}
          {tab === 3 && <EventsTab group={group} />}
          {tab === 4 && <DuesTab group={group} />}
          {tab === 5 && <ContributionsTab group={group} />}
          {tab === 6 && group?.can_manage && <ExecutivesTab group={group} />}
          {tab === 7 && group?.can_manage && <ReportsTab group={group} />}
        </Box>
      </Card>
    </>
  );
}
